import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Tag, Avatar, Spin, Button, Modal, message } from 'antd';
import { UserOutlined, EditOutlined } from '@ant-design/icons';
import { usersApi } from '../services/api-services';
import ImagePicker from '../components/ImagePicker';
import { resolveUrl } from '../utils/url';

const roleMap: Record<string, string> = {
  super_admin: '超级管理员',
  admin: '管理员',
  app_admin: '应用管理员',
  user: '普通用户',
};

const Profile: React.FC = () => {
  const [userInfo, setUserInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [pendingAvatar, setPendingAvatar] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        const users: any = await usersApi.list();
        const current = users.find((u: any) => u.id === stored.id);
        setUserInfo(current || stored);
      } catch {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        setUserInfo(stored);
      }
      setLoading(false);
    };
    fetchUser();
  }, []);

  const openAvatarModal = () => {
    setPendingAvatar(userInfo?.avatar);
    setAvatarModalOpen(true);
  };

  const handleSaveAvatar = async () => {
    setSaving(true);
    // 先更新本地，不依赖 API 成功
    setUserInfo((prev: any) => ({ ...prev, avatar: pendingAvatar }));
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    localStorage.setItem('user', JSON.stringify({ ...stored, avatar: pendingAvatar }));
    window.dispatchEvent(new Event('user-updated'));
    try {
      await usersApi.updateMe({ avatar: pendingAvatar });
      message.success('头像已更新');
    } catch {
      message.warning('头像已在本地更新，但同步服务器失败');
    }
    setAvatarModalOpen(false);
    setSaving(false);
  };

  if (loading || !userInfo) {
    return (
      <div className="apple-page" style={{ textAlign: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  const avatarSrc = userInfo.avatar
    ? (userInfo.avatar.startsWith('/avatars/') ? userInfo.avatar : resolveUrl(userInfo.avatar))
    : undefined;

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">个人中心</h1>
      </div>
      <p className="apple-page-subtitle">查看并编辑当前登录用户的基本信息</p>

      <Card style={{ maxWidth: 700 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24 }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              size={72}
              src={avatarSrc}
              icon={!userInfo.avatar && <UserOutlined />}
              style={{ backgroundColor: 'var(--apple-blue)' }}
            >
              {userInfo.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Button
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              size="small"
              onClick={openAvatarModal}
              style={{
                position: 'absolute', bottom: -4, right: -4,
                width: 24, height: 24, minWidth: 24,
                fontSize: 11, padding: 0,
              }}
            />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{userInfo.name}</div>
            <div style={{ color: 'var(--apple-text-secondary)', marginTop: 4 }}>
              {roleMap[userInfo.role] || userInfo.role}
            </div>
          </div>
        </div>

        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="用户名">{userInfo.username}</Descriptions.Item>
          <Descriptions.Item label="姓名">{userInfo.name}</Descriptions.Item>
          <Descriptions.Item label="角色">
            <Tag color="blue">{roleMap[userInfo.role] || userInfo.role}</Tag>
          </Descriptions.Item>
          {userInfo.title && (
            <Descriptions.Item label="职称">{userInfo.title}</Descriptions.Item>
          )}
          {userInfo.bio && (
            <Descriptions.Item label="简介">{userInfo.bio}</Descriptions.Item>
          )}
          {userInfo.feishu_id && (
            <Descriptions.Item label="飞书 ID">{userInfo.feishu_id}</Descriptions.Item>
          )}
          {userInfo.is_expert && (
            <Descriptions.Item label="专家身份">
              <Tag color="green">已认证专家</Tag>
            </Descriptions.Item>
          )}
          {userInfo.expertise_tags && userInfo.expertise_tags.length > 0 && (
            <Descriptions.Item label="专业领域">
              {userInfo.expertise_tags.map((tag: string) => (
                <Tag key={tag} color="blue" style={{ margin: '2px 4px 2px 0' }}>{tag}</Tag>
              ))}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="创建时间">
            {userInfo.created_at ? new Date(userInfo.created_at).toLocaleString() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Modal
        className="apple-modal"
        title="更换头像"
        open={avatarModalOpen}
        onCancel={() => setAvatarModalOpen(false)}
        onOk={handleSaveAvatar}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        destroyOnHidden
      >
        <div style={{ padding: '8px 0' }}>
          <ImagePicker shape="circle" value={pendingAvatar} onChange={setPendingAvatar} />
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
